import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useTheme } from '../../../contexts/ThemeContext';

const DisplayPreferencesSection = ({ 
  formData, 
  handleInputChange, 
  handleCheckboxChange,
  errors 
}) => {
  const { theme, setTheme } = useTheme();

  const themeOptions = [
    { value: 'light', label: 'Light Mode' },
    { value: 'dark', label: 'Dark Mode' }
  ];

  const refreshIntervalOptions = [
    { value: '5', label: 'Every 5 minutes' },
    { value: '15', label: 'Every 15 minutes' },
    { value: '30', label: 'Every 30 minutes' },
    { value: '60', label: 'Every hour' },
    { value: 'manual', label: 'Manual only' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (International)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' }
  ];

  const timeFormatOptions = [
    { value: '12h', label: '12-hour (AM/PM)' },
    { value: '24h', label: '24-hour (Military)' }
  ];

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-purple/20 glow-subtle">
          <Icon name="Settings" size={20} color="var(--color-accent-purple)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">Display Preferences</h2>
          <p className="text-xs md:text-sm text-muted-foreground font-caption">
            Customize your application experience
          </p>
        </div>
      </div>
      <div className="space-y-6 md:space-y-8">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Appearance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Theme Mode"
              options={themeOptions}
              value={theme}
              onChange={(value) => setTheme(value)}
              placeholder="Select theme"
              className="w-full"
            />
            <div className="flex items-center gap-4 p-4 rounded-xl bg-muted border border-border">
              <div className="flex items-center gap-2">
                <Icon name="Sun" size={20} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Light</span>
              </div>
              <div className="flex-1 h-px bg-border"></div>
              <div className="flex items-center gap-2">
                <Icon name="Moon" size={20} className="text-accent-blue" />
                <span className="text-sm font-medium text-foreground">Dark</span>
              </div>
            </div>
          </div>
        </div>

        <div className="h-px bg-accent-purple/20"></div>

        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Notifications</h3>
          <div className="space-y-3">
            <Checkbox
              label="Email notifications for pay discrepancies"
              description="Receive alerts when differences exceed $100"
              checked={formData?.emailNotifications}
              onChange={(e) => handleCheckboxChange('emailNotifications', e?.target?.checked)}
            />
            <Checkbox
              label="Push notifications for new rotations"
              description="Get notified when new rotation data is available"
              checked={formData?.pushNotifications}
              onChange={(e) => handleCheckboxChange('pushNotifications', e?.target?.checked)}
            />
            <Checkbox
              label="Weekly summary reports"
              description="Receive weekly email summaries of your pay status"
              checked={formData?.weeklySummary}
              onChange={(e) => handleCheckboxChange('weeklySummary', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="h-px bg-accent-purple/20"></div>

        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Data & Sync</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Select
              label="Auto-refresh Interval"
              options={refreshIntervalOptions}
              value={formData?.refreshInterval}
              onChange={(value) => handleInputChange({ target: { name: 'refreshInterval', value } })}
              placeholder="Select refresh interval"
              error={errors?.refreshInterval}
              className="w-full"
            />
            <div className="flex items-end">
              <Checkbox
                label="Auto-sync with company systems"
                description="Automatically fetch latest rotation data"
                checked={formData?.autoSync}
                onChange={(e) => handleCheckboxChange('autoSync', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        <div className="h-px bg-accent-purple/20"></div>

        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Format & Display</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Select
              label="Date Format"
              options={dateFormatOptions}
              value={formData?.dateFormat}
              onChange={(value) => handleInputChange({ target: { name: 'dateFormat', value } })}
              placeholder="Select date format"
              error={errors?.dateFormat}
              className="w-full"
            />
            <Select
              label="Time Format"
              options={timeFormatOptions}
              value={formData?.timeFormat}
              onChange={(value) => handleInputChange({ target: { name: 'timeFormat', value } })}
              placeholder="Select time format"
              error={errors?.timeFormat}
              className="w-full"
            />
          </div>
          <div className="mt-4 space-y-3">
            <Checkbox
              label="Show animations and effects"
              description="Enable holographic and glow effects throughout the app"
              checked={formData?.showAnimations}
              onChange={(e) => handleCheckboxChange('showAnimations', e?.target?.checked)}
            />
            <Checkbox
              label="Compact view mode"
              description="Display more information in less space"
              checked={formData?.compactView}
              onChange={(e) => handleCheckboxChange('compactView', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayPreferencesSection;